import { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid2';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import PropTypes from 'prop-types';
import { Controller, useFormContext, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Text from '../../../elements/text/Text';
import TextInput from '../../../elements/text-input/TextInput';
import { getMaxSeeds } from '../crawlerUtils';

const CrawlStep2 = ({ getMembers }) => {
  const { t } = useTranslation();
  const { control, getValues, watch } = useFormContext();
  const { errors } = useFormState({ control });

  const [members, setMembers] = useState([]);

  const maxSeeds = getMaxSeeds(getValues('jobType'));
  const seeds = watch('seeds');
  const seedsArr = seeds ? seeds.split('\n') : [];
  const remainingSeeds = maxSeeds - seedsArr.length || 0;

  const fetchUsers = async () => {
    const userRes = await getMembers();
    setMembers(userRes);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Limit users from making more seeds than their job allows
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      if (remainingSeeds === 0) {
        event.preventDefault();
      }
    }
  };

  // Prevent user from bypassing limits by copy/pasting
  const handlePaste = (event) => {
    if (remainingSeeds === 0) {
      event.preventDefault();
    }
  };

  return (
    <Box sx={{ overflow: 'hidden' }}>
      <section>
        <Box sx={{ mb: 2 }}>
          <Text caption sx={{ color: 'gray' }}>
            {t('asteriskRequired', { ns: 'fields' })}
          </Text>
        </Box>
        <Grid container spacing={1}>
          <Grid
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <TextInput
              name="jobName"
              label={`${t('jobName', { ns: 'fields' })} *`}
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <FormControl
              variant="outlined"
              size="small"
              fullWidth
              error={!!errors?.user}
            >
              <InputLabel>{`${t('user', { ns: 'fields' })} *`}</InputLabel>
              <Controller
                name="user"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Select
                    {...field}
                    label={`${t('user', { ns: 'fields' })} *`}
                    inputProps={{ 'aria-label': 'user select' }}
                  >
                    {members.map((user) => (
                      <MenuItem key={user.id} value={user.email}>
                        {user.fullName}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              <FormHelperText>{errors?.user?.message}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <TextInput
              name="jobDescription"
              label={t('jobDescription', { ns: 'fields' })}
              control={control}
              errors={errors}
              style={{ marginBottom: '0.5em' }}
            />
          </Grid>
        </Grid>
        {remainingSeeds > -1 && (
          <Grid container>
            <Grid
              style={{ textAlign: 'right' }}
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <Text caption style={{ marginRight: '1em', fontWeight: 'bold' }}>
                {t('stepper.seedsRemaining', {
                  ns: 'crawler',
                  number: remainingSeeds,
                })}
              </Text>
            </Grid>
          </Grid>
        )}
        <Grid container spacing={1}>
          <Grid
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <Controller
              name="seeds"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={seeds}
                  label={`${t('seeds', { ns: 'fields' })} *`}
                  fullWidth
                  variant="outlined"
                  size="small"
                  multiline
                  minRows={8}
                  onKeyDown={handleKeyDown}
                  onPaste={handlePaste}
                  error={!!errors?.seeds}
                  helperText={errors?.seeds?.message}
                  inputProps={{ 'aria-label': 'seeds' }}
                  InputLabelProps={{ shrink: true }}
                  placeholder={t('oneSeedPerLine', { ns: 'descriptions' })}
                />
              )}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <TextInput
              name="subseeds"
              label={t('additionalSeeds', { ns: 'fields' })}
              InputLabelProps={{ shrink: true }}
              placeholder={t('additionalSeedsInfo', { ns: 'descriptions' })}
              control={control}
              errors={errors}
              size={8}
            />
          </Grid>
        </Grid>
      </section>
    </Box>
  );
};

CrawlStep2.propTypes = {
  getMembers: PropTypes.func.isRequired,
};

export default CrawlStep2;
